import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  GridItem,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Text,
  VStack,
  HStack,
  Badge,
  Button,
  Flex,
  Icon,
  useColorModeValue,
  useToast,
  Spinner,
  Alert,
  AlertIcon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Divider,
  Avatar,
  SimpleGrid,
  Tooltip
} from '@chakra-ui/react';
import {
  MdSearch,
  MdGavel,
  MdDescription,
  MdAssignment,
  MdPerson,
  MdCalendarToday,
  MdAttachMoney,
  MdCheckCircle,
  MdArrowForward
} from 'react-icons/md';
import { FaMapMarkerAlt } from 'react-icons/fa';
import api from '../../../../services/api';

const StateCard = ({ state, stats, onStateClick }) => {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value || 0);
  };

  return (
    <Card
      bg={cardBg}
      shadow="sm"
      borderWidth="1px"
      borderColor={borderColor}
      transition="all 0.2s"
      cursor="pointer"
      _hover={{ bg: hoverBg, shadow: 'md', transform: 'translateY(-2px)' }}
      onClick={() => onStateClick(state)}
    >
      <CardBody>
        <VStack align="stretch" spacing={3}>
          <Flex justify="space-between" align="center">
            <HStack>
              <Icon as={FaMapMarkerAlt} color="purple.500" />
              <Heading size="md">{state}</Heading>
            </HStack>
            <Badge colorScheme="purple" fontSize="md" px={2} py={1}>
              {stats.available}
            </Badge>
          </Flex>
          
          <Divider />
          
          <Box>
            <Text fontSize="sm" color="gray.500" mb={1}>Valor Total</Text>
            <Text fontSize="lg" fontWeight="semibold">
              {formatCurrency(stats.valorTotal)}
            </Text>
          </Box>
          
          <Button
            size="sm"
            colorScheme="blue"
            rightIcon={<MdArrowForward />}
            variant="ghost"
          >
            Ver Precatórios
          </Button>
        </VStack>
      </CardBody>
    </Card>
  );
};

const PrecatoriosDisponiveisList = () => {
  const [selectedState, setSelectedState] = useState(null);
  const [availableStats, setAvailableStats] = useState(null);
  const [precatorios, setPrecatorios] = useState([]);
  const [filteredPrecatorios, setFilteredPrecatorios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assigningId, setAssigningId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPrecatorio, setSelectedPrecatorio] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  
  const cardBg = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.700', 'white');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  useEffect(() => {
    fetchAvailableStats();
  }, []);

  useEffect(() => {
    if (selectedState) {
      fetchStatePrecatorios(selectedState);
    }
  }, [selectedState]);

  useEffect(() => {
    filterPrecatorios();
  }, [searchTerm, precatorios]);

  const fetchAvailableStats = async () => {
    try {
      setLoading(true);
      const response = await api.get('/juridico/available/stats');
      console.log('Stats Response:', response);
      // Handle both response structures
      const stats = response.stats || response.data?.stats || null;
      console.log('Setting availableStats to:', stats);
      setAvailableStats(stats);
    } catch (error) {
      console.error('Error fetching available stats:', error);
      toast({
        title: 'Erro ao carregar estatísticas',
        description: 'Não foi possível carregar as estatísticas disponíveis',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchStatePrecatorios = async (state) => {
    try {
      setLoading(true);
      const response = await api.get(`/juridico/available/${state}`);
      console.log('Precatorios Response:', response);
      // Handle both response structures
      const precatoriosData = response.data || response.data?.data || response || [];
      const precatoriosList = Array.isArray(precatoriosData) ? precatoriosData : (precatoriosData.data || []);
      console.log('Setting precatorios to:', precatoriosList);
      setPrecatorios(precatoriosList);
      setFilteredPrecatorios(precatoriosList);
    } catch (error) {
      console.error('Error fetching state precatorios:', error);
      toast({
        title: 'Erro ao carregar precatórios',
        description: `Não foi possível carregar os precatórios de ${state}`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const filterPrecatorios = () => {
    if (!searchTerm) {
      setFilteredPrecatorios(precatorios);
      return;
    }

    const filtered = precatorios.filter(p =>
      p.numero_precatorio?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.cpf_cnpj?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredPrecatorios(filtered);
  };

  const handleAssignPrecatorio = async (precatorioId) => {
    try {
      setAssigningId(precatorioId);
      const response = await api.post(`/juridico/assign/${selectedState}/${precatorioId}`);
      
      toast({
        title: 'Precatório designado',
        description: 'O precatório foi designado para você com sucesso',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      
      // Remove from list
      setPrecatorios(prev => prev.filter(p => p._id !== precatorioId));
      setFilteredPrecatorios(prev => prev.filter(p => p._id !== precatorioId));
      
      // Update stats
      fetchAvailableStats();
      
      onClose();
    } catch (error) {
      console.error('Error assigning precatorio:', error);
      toast({
        title: 'Erro ao designar',
        description: error.response?.data?.message || 'Não foi possível designar o precatório',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setAssigningId(null);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value || 0);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('pt-BR');
  };

  const openPrecatorioDetails = (precatorio) => {
    setSelectedPrecatorio(precatorio);
    onOpen();
  };

  if (loading && !availableStats) {
    return (
      <Flex justify="center" align="center" h="400px">
        <Spinner size="xl" color="blue.500" />
      </Flex>
    );
  }

  return (
    <Box>
      <Heading size="lg" mb={6} color={textColor}>
        Precatórios Disponíveis para Análise Jurídica
      </Heading>

      {!selectedState ? (
        <>
          {/* State Grid View */}
          {console.log('Rendering with availableStats:', availableStats)}
          <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={6}>
            {availableStats && Object.entries(availableStats).map(([state, stats]) => (
              <GridItem key={state}>
                <StateCard 
                  state={state} 
                  stats={stats} 
                  onStateClick={setSelectedState}
                />
              </GridItem>
            ))}
          </Grid>
          
          {(!availableStats || Object.keys(availableStats).length === 0) && (
            <Card bg={cardBg} shadow="sm">
              <CardBody>
                <Alert status="info">
                  <AlertIcon />
                  Não há precatórios disponíveis para análise jurídica no momento
                </Alert>
              </CardBody>
            </Card>
          )}
        </>
      ) : (
        <>
          {/* State Precatorios List */}
          <Card bg={cardBg} shadow="sm">
            <CardHeader>
              <Flex justify="space-between" align="center" mb={4}>
                <HStack>
                  <Button 
                    variant="ghost" 
                    onClick={() => setSelectedState(null)}
                    leftIcon={<MdArrowForward style={{ transform: 'rotate(180deg)' }} />}
                  >
                    Voltar
                  </Button>
                  <Heading size="md" color={textColor}>
                    Precatórios de {selectedState}
                  </Heading>
                  <Badge colorScheme="purple" fontSize="md" px={2} py={1}>
                    {filteredPrecatorios.length} disponíveis
                  </Badge>
                </HStack>
                
                <InputGroup maxW="300px">
                  <InputLeftElement pointerEvents="none">
                    <Icon as={MdSearch} color="gray.400" />
                  </InputLeftElement>
                  <Input
                    placeholder="Buscar precatório..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </InputGroup>
              </Flex>
            </CardHeader>
            
            <CardBody>
              {loading ? (
                <Flex justify="center" align="center" h="200px">
                  <Spinner size="lg" />
                </Flex>
              ) : filteredPrecatorios.length > 0 ? (
                <TableContainer>
                  <Table variant="simple" size="sm">
                    <Thead>
                      <Tr>
                        <Th>Número</Th>
                        <Th>Beneficiário</Th>
                        <Th>CPF/CNPJ</Th>
                        <Th isNumeric>Valor</Th>
                        <Th>Prioridade</Th>
                        <Th>Data Conclusão Docs</Th>
                        <Th>Ações</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {filteredPrecatorios.map((precatorio) => (
                        <Tr key={precatorio._id}>
                          <Td>
                            <Text fontWeight="semibold">{precatorio.numero_precatorio}</Text>
                          </Td>
                          <Td>
                            <HStack>
                              <Avatar size="xs" name={precatorio.nome} />
                              <Text>{precatorio.nome}</Text>
                            </HStack>
                          </Td>
                          <Td>
                            <Text fontSize="sm">{precatorio.cpf_cnpj || 'N/A'}</Text>
                          </Td>
                          <Td isNumeric>
                            <Text fontWeight="semibold">
                              {formatCurrency(precatorio.valor_atualizado)}
                            </Text>
                          </Td>
                          <Td>
                            <Badge colorScheme={precatorio.prioridade === 'Sim' ? 'red' : 'gray'}>
                              {precatorio.prioridade || 'Normal'}
                            </Badge>
                          </Td>
                          <Td>
                            <Text fontSize="sm">
                              {formatDate(precatorio.data_ultima_atualizacao)}
                            </Text>
                          </Td>
                          <Td>
                            <HStack spacing={2}>
                              <Tooltip label="Ver detalhes">
                                <Button
                                  size="xs"
                                  colorScheme="blue"
                                  variant="ghost"
                                  onClick={() => openPrecatorioDetails(precatorio)}
                                >
                                  <Icon as={MdDescription} />
                                </Button>
                              </Tooltip>
                              <Button
                                size="xs"
                                colorScheme="green"
                                leftIcon={<MdGavel />}
                                isLoading={assigningId === precatorio._id}
                                onClick={() => handleAssignPrecatorio(precatorio._id)}
                              >
                                Assumir
                              </Button>
                            </HStack>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert status="info">
                  <AlertIcon />
                  Nenhum precatório encontrado com os filtros aplicados
                </Alert>
              )}
            </CardBody>
          </Card>
        </>
      )}

      {/* Precatorio Details Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Detalhes do Precatório
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedPrecatorio && (
              <VStack align="stretch" spacing={4}>
                <SimpleGrid columns={2} spacing={4}>
                  <Box>
                    <Text fontSize="sm" color={mutedColor}>Número</Text>
                    <Text fontWeight="semibold">{selectedPrecatorio.numero_precatorio}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" color={mutedColor}>Beneficiário</Text>
                    <Text fontWeight="semibold">{selectedPrecatorio.nome}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" color={mutedColor}>CPF/CNPJ</Text>
                    <Text fontWeight="semibold">{selectedPrecatorio.cpf_cnpj || 'N/A'}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" color={mutedColor}>Tipo Documento</Text>
                    <Badge colorScheme="purple">
                      {selectedPrecatorio.document_type_used}
                    </Badge>
                  </Box>
                  <Box>
                    <Text fontSize="sm" color={mutedColor}>Valor Original</Text>
                    <Text fontWeight="semibold">
                      {formatCurrency(selectedPrecatorio.valor_original)}
                    </Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" color={mutedColor}>Valor Atualizado</Text>
                    <Text fontWeight="semibold" color="green.500">
                      {formatCurrency(selectedPrecatorio.valor_atualizado)}
                    </Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" color={mutedColor}>Natureza</Text>
                    <Text>{selectedPrecatorio.natureza || 'N/A'}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" color={mutedColor}>Prioridade</Text>
                    <Badge colorScheme={selectedPrecatorio.prioridade === 'Sim' ? 'red' : 'gray'}>
                      {selectedPrecatorio.prioridade || 'Normal'}
                    </Badge>
                  </Box>
                </SimpleGrid>
                
                <Divider />
                
                <Box>
                  <Text fontSize="sm" color={mutedColor} mb={2}>Informações de Contato</Text>
                  <SimpleGrid columns={2} spacing={2}>
                    <Text fontSize="sm">
                      <strong>Emails:</strong> {selectedPrecatorio.emails_contato?.join(', ') || 'N/A'}
                    </Text>
                    <Text fontSize="sm">
                      <strong>Telefones:</strong> {selectedPrecatorio.telefones_contato?.join(', ') || 'N/A'}
                    </Text>
                  </SimpleGrid>
                </Box>
                
                <Box>
                  <Text fontSize="sm" color={mutedColor} mb={2}>Status Atual</Text>
                  <HStack>
                    <Badge colorScheme="green" fontSize="md" p={2}>
                      <Icon as={MdCheckCircle} mr={1} />
                      Documentos Completos
                    </Badge>
                    <Text fontSize="sm" color={mutedColor}>
                      Checklist 100% concluído
                    </Text>
                  </HStack>
                </Box>
                
                {selectedPrecatorio.observacoes && (
                  <Box>
                    <Text fontSize="sm" color={mutedColor} mb={2}>Observações</Text>
                    <Text fontSize="sm" p={3} bg="gray.50" borderRadius="md">
                      {selectedPrecatorio.observacoes}
                    </Text>
                  </Box>
                )}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Fechar
            </Button>
            <Button
              colorScheme="green"
              leftIcon={<MdGavel />}
              isLoading={assigningId === selectedPrecatorio?._id}
              onClick={() => handleAssignPrecatorio(selectedPrecatorio._id)}
            >
              Assumir Análise
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default PrecatoriosDisponiveisList;